/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import differenceWith from 'lodash.differencewith';
import { action, computed, extendObservable, observable } from 'mobx';
import { createBaseStoreHouse } from 'src/luis/core/stores/baseStoreHouse';
import { LuisId } from 'src/luis/types/Resource';
import { BaseStoreHouse } from 'src/luis/types/stores/core/BaseStoreHouse';
import { ResourceStore } from 'src/luis/types/stores/core/ResourceStore';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { SubtractStore } from 'src/luis/types/SubtractStore';

/**
 * A store house to be composed by all objects that identify
 * as resources in the system.
 */
export const createResourceStoreHouse = <T extends ResourceStore>(predicate: (item: T) => boolean = () => true): ResourceStoreHouse<T> => {
    const baseStore = createBaseStoreHouse();

    const resourcesStore: SubtractStore<ResourceStoreHouse<T> & { _items: T[] }, BaseStoreHouse> = {
        _items: [],

        get items() {
            return this._items.filter(predicate);
        },

        /**
         * Sets the items array with an entirely new one.
         *
         * @param items The array to set.
         */
        setItems(items) {
            this._items = items;
        },

        /**
         * Finds the item with the given id or returns undefined
         * if not found.
         *
         * @param id The id to search for.
         * @returns The item or undefined if not found.
         */
        findById(id) {
            return this._items.find(i => i.id === id);
        },

        /**
         * Adds the given item to the items array.
         *
         * @param item The item to add to the array.
         * @returns The new length of the array.
         */
        add(item, placement = 'front') {
            if (placement === 'front') {
                return this._items.unshift(item);
            }
            return this._items.push(item);
        },

        /**
         * Adds the given items to the items array.
         *
         * @param items The items to add.
         * @returns The new length of the items array.
         */
        batchAdd(items, placement = 'front') {
            if (placement === 'front') {
                return this._items.unshift(...items);
            }
            return this._items.push(...items);
        },

        /**
         * Replaces an existing item with the given item. If
         * the item does not exist, the update is ignored
         * and a false flag is returned.
         *
         * @param item The item to replace with.
         * @returns True if the update operation occurred
         * and false otherwise.
         */
        replace(item) {
            const indexToReplace = this._items.findIndex(i => i.id === item.id);

            if (indexToReplace === -1) {
                return false;
            }

            this._items[indexToReplace] = item;

            return true;
        },

        /**
         * Removes the item identified by the given id.
         *
         * @param id The id to search the item to delete with.
         * @returns True if the item was deleted and false if
         * the item was not found.
         */
        removeById(id) {
            const indexToDelete = this._items.findIndex(i => i.id === id);

            if (indexToDelete === -1) {
                return false;
            }

            this._items.splice(indexToDelete, 1);

            return true;
        },

        /**
         * Batch removes the items with the given ids.
         *
         * @param ids The ids to delete.
         */
        batchRemoveByIds(ids: LuisId[]) {
            this._items = differenceWith(this._items, ids, (item, id) => item.id === id);
        }
    };

    return extendObservable(
        baseStore,
        resourcesStore,
        {
            _items: observable,
            items: computed,
            setItems: action,
            findById: action,
            add: action,
            batchAdd: action,
            replace: action,
            removeById: action,
            batchRemoveByIds: action
        },
        { deep: false }
    );
};
